import { useState } from "react";
import { Navigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";

import { signIn, useSession } from "@/lib/auth-client";
import { CHEETAH_LOGO_SRC } from "@/lib/brand-assets";
import { captureLandingWebsiteIntent } from "@/lib/landing-website-intent";
import { LocaleSwitcher } from "@/components/locale-switcher";

function GoogleGlyph() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

export default function HomePage() {
  const intl = useIntl();
  const { data: sessionData, isPending } = useSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      </div>
    );
  }

  if (sessionData?.session) {
    return <Navigate to="/app" replace />;
  }

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      captureLandingWebsiteIntent(window.location.search);

      const { error: signInError } = await signIn.social({
        provider: "google",
        callbackURL: `${window.location.origin}/app`,
      });

      if (signInError) {
        setError(
          signInError.message ??
            intl.formatMessage({
              id: "home.failedGoogle",
              defaultMessage: "Failed to start Google sign-in.",
            }),
        );
      }
    } catch {
      setError(
        intl.formatMessage({
          id: "home.somethingWentWrong",
          defaultMessage: "Something went wrong. Please try again.",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#fafaf7] text-foreground">
      <div className="pointer-events-none absolute inset-0 " />

      <header className="sticky top-0 z-50 border-b border-[#e8e3d8]/90 bg-[#fafaf7]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5 md:px-16">
          <img
            src={CHEETAH_LOGO_SRC}
            alt={intl.formatMessage({ id: "common.brandName", defaultMessage: "Cheetah" })}
            className="h-5 w-auto"
          />
          <LocaleSwitcher className="rounded-full border border-[#e8e3d8] bg-white/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.06em] text-muted-foreground shadow-soft" />
        </div>
      </header>

      <main className="relative z-10 px-6 pb-32 pt-8 md:px-16 md:pt-12">
        <div className="mx-auto w-full max-w-md">
          <div className="animate-[inspector-fade-in_.45s_ease-out] text-center">
            <h1 className="text-[clamp(24px,3vw,32px)] font-extrabold leading-none tracking-[-0.03em] text-[#132231]">
              <FormattedMessage
                id="home.welcomeToCheetah"
                defaultMessage="Welcome to {brandName}."
                values={{
                  brandName: (
                    <em className="font-thin italic opacity-60">
                      <FormattedMessage id="common.brandName" defaultMessage="Cheetah" />
                    </em>
                  ),
                }}
              />
            </h1>
            <p className="mt-3.5 text-[15px] leading-6 text-muted-foreground">
              <FormattedMessage
                id="home.googleSubtitle"
                defaultMessage="Sign in or create your account to get started."
              />
            </p>
          </div>

          <div
            className="mt-7 animate-[inspector-fade-in_.45s_ease-out_.12s_both] rounded-[22px] border border-[#e8e3d8] bg-white p-7 shadow-[0_16px_40px_rgba(13,31,45,0.06)]"
            data-testid="card-signin"
          >
            <div className="space-y-3">
              <button
                type="button"
                disabled={loading}
                data-testid="button-google-signin"
                className="flex w-full items-center justify-center gap-3 rounded-full border-2 border-[#e8e3d8] bg-white px-5 py-[13px] font-display text-sm font-bold text-[#132231] transition-colors hover:border-black/25 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleGoogleSignIn}
              >
                <GoogleGlyph />
                {loading ? (
                  <FormattedMessage id="home.redirecting" defaultMessage="Redirecting…" />
                ) : (
                  <FormattedMessage id="home.signInGoogle" defaultMessage="Continue with Google" />
                )}
              </button>
              {error ? <p className="text-center text-sm text-destructive">{error}</p> : null}
            </div>
          </div>

          <p className="mt-9 animate-[inspector-fade-in_.45s_ease-out_.24s_both] text-center text-[11px] leading-5 text-muted-foreground">
            <FormattedMessage
              id="home.termsNotice"
              defaultMessage="By continuing, you agree to Cheetah's <terms>Terms</terms> and <privacy>Privacy Policy</privacy>."
              values={{
                terms: (chunks) => (
                  <span className="text-[#132231] underline underline-offset-4">{chunks}</span>
                ),
                privacy: (chunks) => (
                  <span className="text-[#132231] underline underline-offset-4">{chunks}</span>
                ),
              }}
            />
          </p>
        </div>
      </main>
    </div>
  );
}
